import {
  Accordion,
  Box,
  Checkbox,
  Group,
  Image,
  Loader,
  NumberFormatter,
  Paper,
  Radio,
  Stack,
  Text,
} from "@mantine/core";
import { IconSettings } from "@tabler/icons-react";
import { useCallback, useState } from "react";
import { useLanguage } from "../../../../../stores/LanguageStore";
import { FileUploader } from "react-drag-drop-files";
import { read, utils } from "xlsx";
import LineRange from "./LineRange";
import fileSvg from "./FileIllustration.svg";
import { isUTF8 } from "../../../../../functions/utilFunctions";

const fileTypes = ["CSV", "XLS", "XLSX", "TXT"];

const StepImportFile = ({
  files,
  setFiles,
  setData,
  data,
  consent,
  setConsent,
  isUnsub,
  setSheetNames,
  setSheets,
  sheets,
  sheetNames,
  merge,
  setMerge,
  isLinkedToCampaign,
  lines,
  setLines,
  headerLine,
  setHeaderLine,
}) => {
  const lang = useLanguage((s) => s.language);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [noData, setNoData] = useState(false);
  const [pwdProtected, setPwdProtected] = useState(false);

  const handleContacts = useCallback(
    ({ names, isMerge, defaultSheets, defaultHeaderLine, defaultLines }) => {
      const mainNames = names || sheetNames || {};
      const mainSheets = defaultSheets || sheets;
      let _data = [];

      let checked = Object.keys(mainNames)?.filter(
        (x) => mainNames[x] === true
      );

      let header_line = defaultHeaderLine || headerLine;
      let data_lines = defaultLines || lines;

      if (merge || isMerge) {
        checked.forEach((sheetName) => {
          const ws = mainSheets[sheetName];
          if (!ws) return;

          let rows = utils.sheet_to_json(ws, {
            defval: "",
            raw: true,
            rawNumbers: false,
            range: header_line - 1,
            header: isUnsub ? 1 : undefined,
          });

          const slicedRows = rows.slice(
            data_lines[0] - 1,
            data_lines[1] ? data_lines[1] + 1 : undefined
          );

          if (isUnsub) {
            slicedRows.forEach((line) => {
              if (line[0]) {
                _data.push({ value: line[0] });
              }
            });
          } else {
            // Concaténation des données des différentes feuilles/fichiers
            _data = [..._data, ...slicedRows];
          }
        });

        setLoading(false);
        setData(_data);

      } else {
        const ws = mainSheets[checked[0]];
        if (!ws) {
          setLoading(false);
          return;
        }

        let rows = utils.sheet_to_json(ws, {
          defval: "",
          raw: true,
          rawNumbers: false,
          range: header_line - 1,
          header: isUnsub ? 1 : undefined,
        });

        const slicedRows = rows.slice(
          data_lines[0] - 1,
          data_lines[1] ? data_lines[1] + 1 : undefined
        );

        if (isUnsub) {
          slicedRows.forEach((line) => {
            if (line[0]) {
              _data.push({ value: line[0] });
            }
          });
          setLoading(false);
          return setData(_data);
        }

        if (rows.length < 1) {
          setLoading(false);
          return setNoData(true);
        }

        _data = slicedRows;
        setLoading(false);
        setData(_data);
      }
    },
    [headerLine, isUnsub, lines, merge, setData, sheetNames, sheets]
  );

  const handleChange = async (newFiles) => {
    const filesArray = Array.from(newFiles);
    if (filesArray.length === 0) return;

    setLoading(true);
    setFiles(filesArray);
    setPwdProtected(false);

    // Force l'UI en mode fusion par défaut lors d'un nouvel import
    setMerge(true);

    try {
      const results = await Promise.all(
        filesArray.map(async (file) => {
          const fileBuffer = await file.arrayBuffer();

          const isUtf8 = isUTF8(fileBuffer);
          const codepage = isUtf8 ? 65001 : 1252;

          const wb = read(fileBuffer, { codepage: codepage });

          let n = {};
          let uniqueSheets = {};

          wb.SheetNames.forEach((name) => {
            // Nom unique : "Fichier.xlsx - Onglet"
            const uniqueSheetName = `${file.name} - ${name}`;
            n[uniqueSheetName] = true; // Coché par défaut pour le merge
            uniqueSheets[uniqueSheetName] = wb.Sheets[name];
          });

          return { sheets: uniqueSheets, names: n };
        })
      );

      const combinedSheets = results.reduce((acc, curr) => ({ ...acc, ...curr.sheets }), {});
      const combinedNames = results.reduce((acc, curr) => ({ ...acc, ...curr.names }), {});

      setSheets(combinedSheets);
      setSheetNames(combinedNames);

      handleContacts({
        names: combinedNames,
        isMerge: true, // On force le isMerge à true ici aussi
        defaultSheets: combinedSheets
      });

    } catch (e) {
      console.error(e);
      if (e?.message === "File is password-protected") {
        setPwdProtected(true);
        setFiles([]);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack flex={"1 1 auto"}>
      {isLinkedToCampaign && (
        <Text c={"red"} fw={500} mt={"md"}>
          {lang.contact_lists.import.used_in_prog_text}
        </Text>
      )}
      <FileUploader
        handleChange={handleChange}
        name="file"
        classes="file-uploader"
        types={fileTypes}
        hoverTitle={lang.contact_lists.import.step_file.drop_here}
        onDraggingStateChange={(d) => {
          setDragging(d);
        }}
        style={{
          height: "100%",
        }}
        multiple={true}
      >
        <Stack
          align="center"
          justify="center"
          spacing={1}
          style={{
            borderColor: "#00000066",
            borderRadius: 12,
            textAlign: "center",
            cursor: "pointer",
            width: "100%",
            height: "100%",
          }}
          py={32}
        >
          <Image src={fileSvg} w={150} />
          <Text style={{ opacity: dragging ? 0.1 : 1 }} size="lg">
            {lang.contact_lists.import.step_file.file_dnd_placeholder}
          </Text>
          <Text
            style={{ borderRadius: 8 }}
            tt={"lowercase"}
            bg={"primary.0"}
            px={"sm"}
            py={"xs"}
          >
            {fileTypes.map((x) => "." + x + "   ")}
          </Text>
          {loading ? (
            <Text fw={600} fz={16}>
              {lang.contact_lists.import.step_file.loading_file}
            </Text>
          ) : pwdProtected ? (
            <Text fw={600} fz={16} c="red">
              {lang.contact_lists.import.step_file.pwd_protected}
            </Text>
          ) : files?.length > 0 ? (
            noData ? (
              <Text fw={600} fz={16} c={"tertiary.8"}>
                {lang.contact_lists.import.step_file.no_data_in_file_error}
              </Text>
            ) : (
              <Text fw={600} fz={16} c={"tertiary.8"}>
                {lang.contact_lists.import.step_file.selected_file}{" "}
                {files.map((f) => f.name).join(", ")}
              </Text>
            )
          ) : (
            <Text fw={600} fz={16} c="red">
              {lang.contact_lists.import.step_file.no_file_selected}
            </Text>
          )}
        </Stack>
      </FileUploader>

      {sheetNames && Object.keys(sheetNames).length > 1 && (
        <Stack mt={4} gap={"xs"}>
          <Group gap={"xl"}>
            <Text fw={500} fz={15}>
              {lang.contact_lists.import.step_file.multiple_warning}
            </Text>
            <Checkbox
              checked={!merge}
              onChange={(e) => {
                setMerge(false);
                let newSheetNames = { ...sheetNames };
                Object.keys(newSheetNames).forEach((e, i) => {
                  newSheetNames[e] = i === 0;
                });
                setSheetNames(newSheetNames);
                handleContacts({ names: newSheetNames, isMerge: false });
              }}
              label={lang.contact_lists.import.step_file.choose}
            />
            <Checkbox
              checked={merge}
              onChange={(e) => {
                setMerge(true);
                // Quand on repasse en merge, on coche tout par défaut
                let newSheetNames = { ...sheetNames };
                Object.keys(newSheetNames).forEach((n) => {
                  newSheetNames[n] = true;
                });
                setSheetNames(newSheetNames);
                handleContacts({ names: newSheetNames, isMerge: true });
              }}
              label={lang.contact_lists.import.step_file.fusion}
            />
          </Group>

          <Paper component={Group} mah={200} pt={0} style={{ overflowY: 'auto' }}>
            <Text my={4} fw={500}>
              {lang.contact_lists.import.step_file.sheets}
            </Text>
            <Group wrap={"wrap"}>
              {Object.keys(sheetNames).map((sheet) =>
                merge ? (
                  <Checkbox
                    key={sheet}
                    label={`${sheet}`}
                    checked={sheetNames[sheet]}
                    onChange={(e) => {
                      let newSheetNames = {
                        ...sheetNames,
                        [sheet]: !sheetNames[sheet],
                      };
                      handleContacts({ names: newSheetNames, isMerge: true });
                      setSheetNames(newSheetNames);
                    }}
                  />
                ) : (
                  <Radio
                    key={sheet}
                    label={`${sheet}`}
                    checked={sheetNames[sheet]}
                    onChange={(e) => {
                      let newSheetNames = { ...sheetNames };
                      Object.keys(newSheetNames).forEach((n) => {
                        if (n === sheet) {
                          newSheetNames[n] = true;
                        } else newSheetNames[n] = false;
                      });
                      setSheetNames(newSheetNames);
                      handleContacts({ names: newSheetNames, isMerge: false });
                    }}
                  />
                )
              )}
            </Group>
          </Paper>
        </Stack>
      )}

      <Accordion variant="contained" radius={"md"}>
        <Accordion.Item value="more">
          <Accordion.Control disabled={!files?.length} icon={<IconSettings size={18} />}>
            {lang.profile.advanced}
          </Accordion.Control>

          <Accordion.Panel>
            <LineRange
              lines={lines}
              setLines={setLines}
              headerLine={headerLine}
              setHeaderLine={setHeaderLine}
              handleUpdate={handleContacts}
            />
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      {files?.length > 0 && (
        <Group justify="space-between">
          {loading ? (
            <Loader size={16} />
          ) : (
            <b>
              <NumberFormatter
                value={data?.length || 0}
                thousandSeparator=" "
              />{" "}
              {isUnsub
                ? lang.contact_lists.import.step_file.data_found
                : lang.contact_lists.import.step_file.contacts_found}
            </b>
          )}

          {!isUnsub && (
            <Checkbox
              size="sm"
              label={lang.contact_lists.import.confirm_text}
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
            />
          )}
        </Group>
      )}
    </Stack>
  );
};

export default StepImportFile;